"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "../../lib/utils";
import { usePortalScope } from "../../lib/portal-scope";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverClose = PopoverPrimitive.Close;

/**
 * Popover content panel rendered inside a portal.
 *
 * @param props - Accepts all `PopoverPrimitive.Content` props including `ref`.
 * @param props.align - Horizontal alignment relative to the trigger (default `"center"`).
 * @param props.sideOffset - Distance in px from the trigger edge (default `4`).
 * @param props.className - Merged with the default popover styles via `cn`.
 */
function PopoverContent({
	className,
	align = "center",
	sideOffset = 4,
	ref,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
	const container = usePortalScope();
	return (
		<PopoverPrimitive.Portal container={container}>
			<PopoverPrimitive.Content
				ref={ref}
				align={align}
				sideOffset={sideOffset}
				className={cn(
					"z-[2000] w-72 rounded-md border border-neutral-200 bg-popover p-4 text-neutral-900 shadow-md outline-none",
					"dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50",
					"data-[state=open]:animate-in data-[state=closed]:animate-out",
					"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
					"data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
					"data-[side=bottom]:slide-in-from-top-2",
					"data-[side=left]:slide-in-from-right-2",
					"data-[side=right]:slide-in-from-left-2",
					"data-[side=top]:slide-in-from-bottom-2",
					"max-h-[calc(var(--radix-popover-content-available-height)-1rem)] overflow-y-auto",
					className,
				)}
				{...props}
			/>
		</PopoverPrimitive.Portal>
	);
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose };
